import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCRM } from '../../contexts/CRMContext';
import { useBranding } from '../../contexts/BrandingContext';
import { useSubscription, FeatureGate } from '../../contexts/SubscriptionContext';
import { insightsAPI } from '../../utils/crmApi';
import { EmptyStates } from '../ui/empty-state';
import {
    MagnifyingGlassIcon,
    FunnelIcon,
    BookOpenIcon,
    BuildingOfficeIcon,
    TagIcon,
    StarIcon,
    EyeIcon,
    ShareIcon,
    PlusIcon,
    PencilIcon,
    TrashIcon,
    ArrowPathIcon,
    SparklesIcon,
    DocumentTextIcon,
    UserGroupIcon,
    GlobeAltIcon,
    PhoneIcon,
    EnvelopeIcon,
    MapPinIcon,
    ClockIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const Insights = () => {
    const { currentAgency } = useCRM();
    const { branding } = useBranding();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('knowledge');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [selectedTags, setSelectedTags] = useState([]);
    const [sortBy, setSortBy] = useState('recent');
    const [loading, setLoading] = useState(false);
    const [knowledgeItems, setKnowledgeItems] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [categories, setCategories] = useState([]);
    const [tags, setTags] = useState([]);

    // Load real data from API
    useEffect(() => {
        loadInsightsData();
    }, []);

    const loadInsightsData = async () => {
        setLoading(true);
        try {
            const [knowledgeResponse, vendorsResponse, categoriesResponse, tagsResponse] = await Promise.all([
                insightsAPI.getKnowledgeItems(),
                insightsAPI.getVendors(),
                insightsAPI.getCategories(),
                insightsAPI.getTags()
            ]);

            setKnowledgeItems(Array.isArray(knowledgeResponse.data) ? knowledgeResponse.data : []);
            setVendors(Array.isArray(vendorsResponse.data) ? vendorsResponse.data : []);
            setCategories(Array.isArray(categoriesResponse.data) ? categoriesResponse.data : []);
            setTags(Array.isArray(tagsResponse.data) ? tagsResponse.data : []);
        } catch (error) {
            console.error('Error loading insights data:', error);
        } finally {
            setLoading(false);
        }
    };

    const filteredKnowledgeItems = useMemo(() => {
        return knowledgeItems.filter(item => {
            const matchesSearch = item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                item.content.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
            const matchesTags = selectedTags.length === 0 || 
                              selectedTags.some(tag => item.tags.includes(tag));
            
            return matchesSearch && matchesCategory && matchesTags;
        }).sort((a, b) => {
            switch (sortBy) {
                case 'recent':
                    return new Date(b.createdAt) - new Date(a.createdAt);
                case 'popular':
                    return b.views - a.views;
                case 'rating':
                    return b.rating - a.rating;
                case 'title':
                    return a.title.localeCompare(b.title);
                default:
                    return 0;
            }
        });
    }, [knowledgeItems, searchTerm, selectedCategory, selectedTags, sortBy]);

    const filteredVendors = useMemo(() => {
        return vendors.filter(vendor => {
            const matchesSearch = vendor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                vendor.description.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesCategory = selectedCategory === 'all' || vendor.category === selectedCategory;
            
            return matchesSearch && matchesCategory;
        }).sort((a, b) => {
            switch (sortBy) {
                case 'rating':
                    return b.rating - a.rating;
                case 'reviews':
                    return b.reviews - a.reviews;
                case 'name':
                    return a.name.localeCompare(b.name);
                default:
                    return 0;
            }
        });
    }, [vendors, searchTerm, selectedCategory, sortBy]);

    const handleTagToggle = (tagId) => {
        setSelectedTags(prev => 
            prev.includes(tagId) 
                ? prev.filter(t => t !== tagId)
                : [...prev, tagId]
        );
    };

    const handleCreateItem = () => {
        navigate('/insights/create');
    };

    const handleEditItem = (id) => {
        navigate(`/insights/edit/${id}`);
    };

    const handleDeleteItem = (id) => {
        // Implement delete functionality
        console.log('Delete item:', id);
    };

    const handleViewItem = (id) => {
        navigate(`/insights/view/${id}`);
    };

    const SkeletonLoader = () => (
        <div className="animate-pulse">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                    <div key={i} className="bg-card rounded-lg shadow-sm border border-border p-6">
                        <div className="h-4 bg-muted rounded w-3/4 mb-3"></div>
                        <div className="h-3 bg-muted rounded w-1/2 mb-4"></div>
                        <div className="h-3 bg-muted rounded w-full mb-2"></div>
                        <div className="h-3 bg-muted rounded w-2/3"></div>
                    </div>
                ))}
            </div>
        </div>
    );

    return (
        <div className="flex flex-col h-full bg-background">
            {/* Header */}
            <div className="bg-card border-b border-border px-6 py-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-foreground">Insights & Directory</h1>
                        <p className="text-muted-foreground mt-1">Knowledge base and vendor directory</p>
                    </div>
                    <button
                        onClick={handleCreateItem}
                        className="inline-flex items-center gap-2 px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-offset-2"
                        style={{ 
                            backgroundColor: branding?.primaryColor || '#3b82f6',
                            '--tw-ring-color': branding?.primaryColor || '#3b82f6'
                        }}
                    >
                        <PlusIcon className="h-5 w-5" />
                        Add Item
                    </button>
                </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-border bg-card">
                <nav className="flex space-x-8 px-6">
                    {[
                        { id: 'knowledge', name: 'Knowledge Base', icon: BookOpenIcon },
                        { id: 'vendors', name: 'Vendor Directory', icon: BuildingOfficeIcon }
                    ].map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                                activeTab === tab.id
                                    ? 'border-primary text-foreground'
                                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
                            }`}
                        >
                            <tab.icon className="h-5 w-5" />
                            {tab.name}
                        </button>
                    ))}
                </nav>
            </div>

            {/* Filters */}
            <div className="bg-card border-b border-border px-6 py-4">
                <div className="flex flex-col lg:flex-row gap-4">
                    {/* Search */}
                    <div className="flex-1">
                        <div className="relative">
                            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                            <input
                                type="text"
                                placeholder="Search knowledge base and vendors..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-input rounded-lg bg-background text-foreground focus:ring-2 focus:ring-ring focus:border-ring"
                            />
                        </div>
                    </div>

                    {/* Category Filter */}
                    <div className="lg:w-64">
                        <select
                            value={selectedCategory}
                            onChange={(e) => setSelectedCategory(e.target.value)}
                            className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground focus:ring-2 focus:ring-ring focus:border-ring"
                        >
                            <option value="all">All Categories</option>
                            {Array.isArray(categories) && categories.map(category => (
                                <option key={category.id || category._id} value={category.id || category._id}>
                                    {category.name} ({category.count || 0})
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Sort */}
                    <div className="lg:w-48">
                        <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground focus:ring-2 focus:ring-ring focus:border-ring"
                        >
                            <option value="recent">Most Recent</option>
                            <option value="popular">Most Popular</option>
                            <option value="rating">Highest Rated</option>
                            <option value="title">Alphabetical</option>
                        </select>
                    </div>
                </div>

                {/* Tags */}
                {activeTab === 'knowledge' && (
                    <div className="mt-4">
                        <div className="flex flex-wrap gap-2">
                            {Array.isArray(tags) && tags.map(tag => (
                                <button
                                    key={tag.id || tag._id}
                                    onClick={() => handleTagToggle(tag.id || tag._id)}
                                    className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm transition-colors ${
                                        selectedTags.includes(tag.id || tag._id)
                                            ? 'bg-primary text-primary-foreground'
                                            : 'bg-muted text-foreground hover:bg-muted/80'
                                    }`}
                                >
                                    <TagIcon className="h-4 w-4" />
                                    {tag.name} ({tag.count || 0})
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Content */}
            <div className="flex-1 p-6">
                {loading ? (
                    <SkeletonLoader />
                ) : (
                    <>
                        {activeTab === 'knowledge' && (
                            filteredKnowledgeItems.length === 0 ? (
                                <div className="py-12">
                                    {searchTerm || selectedCategory !== 'all' || selectedTags.length > 0 ? (
                                        <div className="text-center py-12">
                                            <h3 className="text-lg font-medium text-foreground mb-2">No knowledge items found</h3>
                                            <p className="text-muted-foreground">Try adjusting your search or filter criteria</p>
                                        </div>
                                    ) : (
                                        <EmptyStates.NoInsights 
                                            onCreateItem={handleCreateItem}
                                            branding={branding}
                                        />
                                    )}
                                </div>
                            ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {filteredKnowledgeItems.map(item => (
                                    <div key={item.id} className="bg-card rounded-lg shadow-sm border border-border p-6 hover:shadow-md transition-shadow">
                                        <div className="flex items-start justify-between mb-3">
                                            <div className="flex items-center gap-2">
                                                <DocumentTextIcon className="h-5 w-5 text-muted-foreground" />
                                                <span className="text-sm text-muted-foreground capitalize">{item.type}</span>
                                                {item.featured && (
                                                    <StarIcon className="h-4 w-4 text-yellow-500 fill-current" />
                                                )}
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <button
                                                    onClick={() => handleEditItem(item.id)}
                                                    className="p-1 text-muted-foreground hover:text-foreground"
                                                >
                                                    <PencilIcon className="h-4 w-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteItem(item.id)}
                                                    className="p-1 text-muted-foreground hover:text-red-600"
                                                >
                                                    <TrashIcon className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </div>

                                        <h3 className="font-semibold text-foreground mb-2 line-clamp-2">
                                            {item.title}
                                        </h3>
                                        
                                        <p className="text-muted-foreground text-sm mb-4 line-clamp-3">
                                            {item.content}
                                        </p>

                                        <div className="flex items-center gap-2 mb-3">
                                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-muted text-foreground">
                                                {item.category}
                                            </span>
                                            <div className="flex items-center gap-1">
                                                <StarIcon className="h-4 w-4 text-yellow-500 fill-current" />
                                                <span className="text-sm text-muted-foreground">{item.rating}</span>
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <EyeIcon className="h-4 w-4 text-muted-foreground" />
                                                <span className="text-sm text-muted-foreground">{item.views}</span>
                                            </div>
                                        </div>

                                        <div className="flex flex-wrap gap-1 mb-4">
                                            {item.tags.slice(0, 3).map(tag => (
                                                <span key={tag} className="inline-flex items-center px-2 py-1 rounded text-xs bg-muted text-foreground">
                                                    {tag}
                                                </span>
                                            ))}
                                            {item.tags.length > 3 && (
                                                <span className="text-xs text-muted-foreground">+{item.tags.length - 3} more</span>
                                            )}
                                        </div>

                                        <div className="flex items-center justify-between">
                                            <span className="text-xs text-muted-foreground">
                                                By {item.author} • {new Date(item.createdAt).toLocaleDateString()}
                                            </span>
                                            <button
                                                onClick={() => handleViewItem(item.id)}
                                                className="text-muted-foreground hover:text-foreground text-sm font-medium"
                                            >
                                                View Details →
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            )
                        )}

                        {activeTab === 'vendors' && (
                            filteredVendors.length === 0 ? (
                                <div className="py-12">
                                    {searchTerm || selectedCategory !== 'all' ? (
                                        <div className="text-center py-12">
                                            <h3 className="text-lg font-medium text-foreground mb-2">No vendors found</h3>
                                            <p className="text-muted-foreground">Try adjusting your search or filter criteria</p>
                                        </div>
                                    ) : (
                                        <EmptyStates.NoVendors 
                                            onCreateItem={handleCreateItem}
                                            branding={branding}
                                        />
                                    )}
                                </div>
                            ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {filteredVendors.map(vendor => (
                                    <div key={vendor.id} className="bg-card rounded-lg shadow-sm border border-border p-6 hover:shadow-md transition-shadow">
                                        <div className="flex items-start justify-between mb-3">
                                            <div className="flex items-center gap-2">
                                                <BuildingOfficeIcon className="h-5 w-5 text-muted-foreground" />
                                                <span className="text-sm text-muted-foreground">{vendor.type}</span>
                                                {vendor.verified && (
                                                    <CheckCircleIcon className="h-4 w-4 text-green-500" />
                                                )}
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <button
                                                    onClick={() => handleEditItem(vendor.id)}
                                                    className="p-1 text-muted-foreground hover:text-foreground"
                                                >
                                                    <PencilIcon className="h-4 w-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteItem(vendor.id)}
                                                    className="p-1 text-muted-foreground hover:text-red-600"
                                                >
                                                    <TrashIcon className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </div>

                                        <h3 className="font-semibold text-foreground mb-2">
                                            {vendor.name}
                                        </h3>
                                        
                                        <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
                                            {vendor.description}
                                        </p>

                                        <div className="space-y-2 mb-4">
                                            <div className="flex items-center gap-2 text-sm">
                                                <MapPinIcon className="h-4 w-4 text-muted-foreground" />
                                                <span className="text-muted-foreground">{vendor.location}</span>
                                            </div>
                                            <div className="flex items-center gap-2 text-sm">
                                                <PhoneIcon className="h-4 w-4 text-muted-foreground" />
                                                <span className="text-muted-foreground">{vendor.contact.phone}</span>
                                            </div>
                                            <div className="flex items-center gap-2 text-sm">
                                                <EnvelopeIcon className="h-4 w-4 text-muted-foreground" />
                                                <span className="text-muted-foreground">{vendor.contact.email}</span>
                                            </div>
                                        </div>

                                        <div className="flex items-center justify-between mb-3">
                                            <div className="flex items-center gap-1">
                                                <StarIcon className="h-4 w-4 text-yellow-500 fill-current" />
                                                <span className="text-sm text-muted-foreground">{vendor.rating}</span>
                                                <span className="text-xs text-muted-foreground">({vendor.reviews} reviews)</span>
                                            </div>
                                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-muted text-foreground">
                                                {vendor.category}
                                            </span>
                                        </div>

                                        <div className="flex flex-wrap gap-1 mb-4">
                                            {vendor.specialties.slice(0, 2).map(specialty => (
                                                <span key={specialty} className="inline-flex items-center px-2 py-1 rounded text-xs bg-muted text-foreground">
                                                    {specialty}
                                                </span>
                                            ))}
                                            {vendor.specialties.length > 2 && (
                                                <span className="text-xs text-muted-foreground">+{vendor.specialties.length - 2} more</span>
                                            )}
                                        </div>

                                        <div className="flex items-center justify-between">
                                            <span className="text-xs text-muted-foreground">
                                                {vendor.languages.join(', ')}
                                            </span>
                                            <button
                                                onClick={() => handleViewItem(vendor.id)}
                                                className="text-muted-foreground hover:text-foreground text-sm font-medium"
                                            >
                                                View Details →
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            )
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default Insights;
